import {Injectable, signal, WritableSignal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {User} from 'patja-api';
import {CompanyUserFeature} from '../utils/company-user-ui-features';
import {CompaniesService} from './companies.service';

interface UiFeaturesResponse {
  enabledFeatures?: string[] | null;
}

type LoadState = 'idle' | 'loading' | 'loaded' | 'error';

@Injectable({
  providedIn: 'root',
})
export class CompanyUserUiFeaturesService {
  private state: WritableSignal<LoadState> = signal('idle');
  private enabled: WritableSignal<Set<CompanyUserFeature> | null> = signal(null);
  /** Cache key: company id for company users, 'accountant:<officeId>' for accountants. */
  private loadedKey: WritableSignal<string | null> = signal(null);

  constructor(
    private http: HttpClient,
    private companiesService: CompaniesService,
  ) {}

  async loadForCompany(companyId: string): Promise<void> {
    if (!companyId) return;
    await this.load(companyId, `company/${companyId}/my-ui-features`);
  }

  async loadForSelectedCompany(): Promise<void> {
    const companyId = this.companiesService.getSelectedCompany()()?.id;
    if (!companyId) return;
    await this.loadForCompany(companyId);
  }

  /**
   * Union of enabled modules across the accountant's visible client companies
   * (scoped to the active accounting office on the backend via the JWT).
   */
  async loadForAccountant(activeAccountingOfficeId?: string | null): Promise<void> {
    const key = `accountant:${activeAccountingOfficeId ?? 'all'}`;
    await this.load(key, 'company/accountant-ui-features');
  }

  /** Loads the correct feature set for the user's current context (company user or accountant). */
  async loadForCurrentContext(user: User | null): Promise<void> {
    if (!user) return;
    if (user.isAccountant()) {
      await this.loadForAccountant(user.activeAccountingOfficeId ?? null);
      return;
    }
    if (user.isCustomer()) {
      await this.loadForSelectedCompany();
    }
  }

  private async load(cacheKey: string, url: string): Promise<void> {
    if (this.loadedKey() === cacheKey && this.state() === 'loaded') {
      return;
    }

    this.state.set('loading');
    this.loadedKey.set(cacheKey);

    try {
      const res = await firstValueFrom(this.http.get<UiFeaturesResponse>(url));

      const set = new Set(
        (res.enabledFeatures || [])
          .filter((x: string) =>
            Object.values(CompanyUserFeature).includes(x as CompanyUserFeature),
          )
          .map(x => x as CompanyUserFeature),
      );

      this.enabled.set(set);
      this.state.set('loaded');
    } catch {
      this.enabled.set(null);
      this.state.set('error');
    }
  }

  isEnabled(feature: CompanyUserFeature): boolean {
    const state = this.state();
    if (state === 'error') return false;
    if (state !== 'loaded') return false;

    const enabled = this.enabled();
    if (!enabled) return true;
    return enabled.has(feature);
  }
}
